import React from 'react';
import { DataTable, TableBody } from '@/components/ui/data-table';
import { DataTableColumn } from '@/components/ui/data-table/DataTableContext';
import { TableHeader, TableRow, TableHead } from '@/components/ui/table';
import { usePagination } from '@/hooks/usePagination';
import TablePagination from '@/components/ui/tablePagination';

interface SubscriptionDetail {
  stripe_subscription_id: string;
  customer_email: string;
  current_amount: number;
  calculated_new_price: number;
  grace_period_end: string;
  subscription_interval: string;
  processing_fee: number;
  fees_breakdown: {
    grossAmount: number;
    stripeFee: number;
    keepMoreFee: number;
    applicationFee: number;
    currentProcessingFee: number;
  };
}

interface SubscriptionDetailsListProps {
  subscriptions: SubscriptionDetail[];
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string) => string;
}

const columns: DataTableColumn[] = [
  { id: 'customer', label: 'Customer', align: 'left' },
  { id: 'subscription', label: 'Subscription', align: 'left' },
  { id: 'amount', label: 'Amount', align: 'left' },
  { id: 'fees', label: 'Fees', align: 'left' },
  { id: 'period', label: 'Period', align: 'left' },
  { id: 'next_bill', label: 'Next Bill', align: 'left' },
  { id: 'new_amount', label: 'New Amount', align: 'left' },
];

const formatBillingInterval = (interval?: string) => {
  if (!interval) return 'N/A';
  const intervalMap: Record<string, string> = {
    'day': 'Daily',
    'week': 'Weekly',
    'month': 'Monthly',
    'year': 'Yearly',
  };
  return intervalMap[interval.toLowerCase()] || interval;
};

export const SubscriptionDetailsList: React.FC<SubscriptionDetailsListProps> = ({
  subscriptions,
  formatCurrency,
  formatDate
}) => {
  const pagination = usePagination<SubscriptionDetail>({
    data: subscriptions,
    itemsPerPage: 5
  });

  return (
    <div className="border rounded-lg overflow-hidden">
      <DataTable>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.id}
                className={`px-4 py-3 text-xs uppercase font-medium ${
                  column.align === 'center' ? 'text-center' :
                  column.align === 'right' ? 'text-right' : ''
                }`}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagination.paginatedData.length === 0 ? (
            <DataTable.Empty colSpan={columns.length} message="No subscriptions found" />
          ) : (
            pagination.paginatedData.map((sub) => (
              <DataTable.Row key={sub.stripe_subscription_id}>
                {/* Customer */}
                <DataTable.Cell className="min-w-[160px]">
                  <div className="space-y-0.5">
                    <div className="font-medium text-sm">
                      {sub.customer_email?.split('@')[0] || 'Unknown'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {sub.customer_email}
                    </div>
                  </div>
                </DataTable.Cell>

                {/* Subscription */}
                <DataTable.Cell className="min-w-[160px]">
                  <div className="font-medium text-sm truncate">
                    Subscription {sub.stripe_subscription_id.slice(-6)}
                  </div>
                </DataTable.Cell>

                {/* Amount */}
                <DataTable.Cell className="whitespace-nowrap text-sm">
                  {formatCurrency(sub.current_amount)}
                </DataTable.Cell>

                {/* Fees */}
                <DataTable.Cell className="whitespace-nowrap text-sm">
                  {formatCurrency(sub.processing_fee)}
                </DataTable.Cell>

                {/* Period */}
                <DataTable.Cell className="whitespace-nowrap text-sm">
                  {formatBillingInterval(sub.subscription_interval)}
                </DataTable.Cell>

                {/* Next Bill */}
                <DataTable.Cell className="whitespace-nowrap text-sm">
                  {formatDate(sub.grace_period_end)}
                </DataTable.Cell>

                {/* New Amount */}
                <DataTable.Cell className="whitespace-nowrap text-sm">
                  <span className="font-medium text-green-600">
                    {formatCurrency(sub.calculated_new_price)}
                  </span>
                </DataTable.Cell>
              </DataTable.Row>
            ))
          )}
        </TableBody>
      </DataTable>

      <TablePagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        itemsPerPage={pagination.itemsPerPage}
        totalItems={pagination.totalItems}
        startItem={pagination.startItem}
        endItem={pagination.endItem}
        hasNextPage={pagination.hasNextPage}
        hasPreviousPage={pagination.hasPreviousPage}
        onPageChange={pagination.goToPage}
        onItemsPerPageChange={pagination.setItemsPerPage}
        onFirstPage={pagination.goToFirstPage}
        onLastPage={pagination.goToLastPage}
        onNextPage={pagination.goToNextPage}
        onPreviousPage={pagination.goToPreviousPage}
      />
    </div>
  );
};
